/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {ShoppingCartItem} from "types/entities/ShoppingCartItem";
import {Article} from "types/entities";
import {useDispatch} from "useDispatch";
import {AddToCartRequest, UpdateShoppingCartItemRequest} from "types/entities/Cart";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import {ShoppingCart} from "types/entities/ShoppingCart";
import {useTypedSelector} from "helpers/reducers";
import CartAction from "store/cart/CartAction";

interface useShoppingCartProps {}

const useShoppingCart = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const cart: ShoppingCart = useTypedSelector(state => state.cartState.cart) as ShoppingCart;

    //used in each articleRow as a representation of which ShoppingCartItem to update or which Article to add
    const selectArticleHandler = async (
        shoppingCartItem: ShoppingCartItem | null,
        article: Article,
        account_id: number,
        quantity: number,
        forceRemove: boolean = false
    ): Promise<ShoppingCart> => {
        // do I want to add and article always or always force update / remove - Registration Product selection
        if (forceRemove && shoppingCartItem) {
            console.log("fetchRemoveCartItem when forceRemove");
            await fetchRemoveCartItem(shoppingCartItem);
        }

        //Checkout additional products
        if (!forceRemove && shoppingCartItem) {
            console.log("updateCartItem");
            return updateCartItem(shoppingCartItem, quantity);
        } else {
            console.log("fetchSetCartItem", article.id);
            return fetchSetCartItem(account_id, article, quantity).then(r => {
                return r;
            });
        }
    };

    /**
     * Simple remove ShoppingCartItem from a cart
     * @param cartItemForRemove ShoppingCartItem
     */
    const fetchRemoveCartItem = async (cartItemForRemove: ShoppingCartItem): Promise<ShoppingCart> => {
        return await dispatch(CartAction.fetchRemoveCartItem(cartItemForRemove));
    };

    /** Simple fetch set cart item with return of dispatch ShoppingCart
     *
     */
    const fetchSetCartItem = async (account_id: number, article: Article, quantity: number): Promise<ShoppingCart> => {
        const item: AddToCartRequest = {
            shopping_cart_id: cart?.id,
            driver_id: account_id,
            article_id: article.id,
            identifier: String(article.id),
            quantity: quantity,
            ticket_type: article.ticket_type,
        };
        toast.dismiss();
        return await dispatch(CartAction.fetchSetCartItem(item)).then((r: ShoppingCart) => {
            toast.success(t("Product selected."));
            return r;
        });
    };

    /** Simple fetch update cart item with return of dispatch ShoppingCart, always ending up with updating a ShoppingCartItem with a new quantity
     */
    const updateCartItem = async (selectedCartItem: ShoppingCartItem, quantity: number): Promise<ShoppingCart> => {
        const item: UpdateShoppingCartItemRequest = {
            shopping_cart_item_id: selectedCartItem.id,
            shopping_cart_id: cart?.id,
            quantity: quantity,
            ticket_type: selectedCartItem.article.ticket_type,
        };
        return await dispatch(CartAction.fetchUpdateCartItem(item)).then((r: ShoppingCart) => {
            toast.success(t("Product updated."));
            return r;
        });
    };

    return {
        selectArticleHandler: selectArticleHandler,
        fetchSetCartItem: fetchSetCartItem,
        fetchRemoveCartItem: fetchRemoveCartItem,
        updateCartItem: updateCartItem,
    };
};
export default useShoppingCart;
